import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  QueryDocumentSnapshot,
  DocumentData,
  DocumentSnapshot,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Bundle,
  BundleItem,
  BundlePriceMap,
  BundlePurchaseTypesConfig,
  BundleStripeInfo,
  SupportedCurrency,
  BundleDefaultAddress
} from '../types';

const COLLECTION_NAME = 'bundles';
const SUPPORTED_CURRENCIES: SupportedCurrency[] = ['usd', 'cad', 'gbp'];

interface BundleInput {
  name: string;
  slug: string;
  description?: string;
  images: string[];
  heroImage?: string;
  tags?: string[];
  category?: string;
  displayOrder?: number | null;
  purchaseTypes: BundlePurchaseTypesConfig;
  prices: BundlePriceMap;
  items: BundleItem[];
  shippingConfig?: Bundle['shippingConfig'];
  addressPolicy?: Bundle['addressPolicy'];
  defaultAddress?: BundleDefaultAddress | null;
  published: boolean;
  disabled?: boolean;
  visibleFrom?: Date | null;
  visibleTo?: Date | null;
}

interface BundlePaginationResult {
  bundles: Bundle[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

const toNumberOrNull = (value: unknown): number | null => {
  if (value == null) return null;
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (typeof numeric !== 'number' || Number.isNaN(numeric)) {
    return null;
  }
  return numeric;
};

const normalizeDisplayOrder = (value: unknown): number | null => {
  const numeric = toNumberOrNull(value);
  if (numeric == null) return null;
  if (!Number.isFinite(numeric) || !Number.isInteger(numeric) || numeric < 1) {
    return null;
  }
  return numeric;
};

const normalizePrices = (prices: any): BundlePriceMap => {
  const result: BundlePriceMap = {
    usd: { oneTime: null, subscription: null },
    cad: { oneTime: null, subscription: null },
    gbp: { oneTime: null, subscription: null }
  };

  SUPPORTED_CURRENCIES.forEach((currency) => {
    const currencyPrice = prices?.[currency] || {};
    result[currency] = {
      oneTime: toNumberOrNull(currencyPrice.oneTime),
      subscription: toNumberOrNull(currencyPrice.subscription)
    };
  });

  return result;
};

const processBundleDoc = (docSnap: DocumentSnapshot<DocumentData>): Bundle => {
  const data = docSnap.data();

  const convertTimestamp = (value: any): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (value?.toDate && typeof value.toDate === 'function') {
      return value.toDate();
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  };

  const normalizeStripeInfo = (stripeInfo: any): BundleStripeInfo | undefined => {
    if (!stripeInfo) return undefined;

    const normalizedPrices: Record<SupportedCurrency, any> = {
      usd: {},
      cad: {},
      gbp: {}
    };

    SUPPORTED_CURRENCIES.forEach((currency) => {
      const priceInfo = stripeInfo.prices?.[currency];
      normalizedPrices[currency] = {
        oneTimeId: priceInfo?.oneTimeId || null,
        subscriptionId: priceInfo?.subscriptionId || null
      };
    });

    return {
      productId: stripeInfo.productId || undefined,
      prices: normalizedPrices,
      lastSyncAt: convertTimestamp(stripeInfo.lastSyncAt) || null,
      needsStripeSync: stripeInfo.needsStripeSync ?? false
    };
  };

  const bundle: Bundle = {
    id: docSnap.id,
    name: data.name || '',
    slug: data.slug || docSnap.id,
    description: data.description || '',
    images: data.images || [],
    heroImage: data.heroImage || undefined,
    tags: data.tags || [],
    category: data.category || undefined,
    displayOrder: normalizeDisplayOrder(data.displayOrder),
    purchaseTypes: {
      oneTime: data.purchaseTypes?.oneTime ?? false,
      subscription: data.purchaseTypes?.subscription ?? false
    },
    prices: normalizePrices(data.prices || {}),
    items: Array.isArray(data.items)
      ? data.items.map((item: any) => ({
          ...item,
          brand: item.brand ?? null,
          netWeight: typeof item.netWeight === 'number' ? item.netWeight : item.netWeight ? Number(item.netWeight) : null,
          weightUnit: item.weightUnit ?? null
        }))
      : [],
    shippingConfig: data.shippingConfig || undefined,
    addressPolicy: data.addressPolicy || 'fixed',
    defaultAddress: data.defaultAddress || null,
    published: data.published ?? false,
    disabled: data.disabled ?? false,
    visibleFrom: convertTimestamp(data.visibleFrom) || null,
    visibleTo: convertTimestamp(data.visibleTo) || null,
    stripe: normalizeStripeInfo(data.stripe),
    activeSubscriptionCount: data.activeSubscriptionCount || 0,
    totalSubscribers: data.totalSubscribers || 0,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt)
  };

  return bundle;
};

const ensurePriceStructure = (prices: BundlePriceMap): BundlePriceMap => {
  const result: BundlePriceMap = {
    usd: { oneTime: null, subscription: null },
    cad: { oneTime: null, subscription: null },
    gbp: { oneTime: null, subscription: null }
  };

  SUPPORTED_CURRENCIES.forEach((currency) => {
    const currencyPrice = prices?.[currency] || {};
    result[currency] = {
      oneTime: toNumberOrNull(currencyPrice.oneTime),
      subscription: toNumberOrNull(currencyPrice.subscription)
    };
  });

  return result;
};

export const fetchBundles = async (pageSize = 20, lastDoc?: QueryDocumentSnapshot<DocumentData>): Promise<BundlePaginationResult> => {
  const bundlesRef = collection(db, COLLECTION_NAME);
  const bundleQuery = lastDoc
    ? query(bundlesRef, orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(pageSize))
    : query(bundlesRef, orderBy('createdAt', 'desc'), limit(pageSize));

  const snapshot = await getDocs(bundleQuery);
  const bundles = snapshot.docs.map(processBundleDoc);

  return {
    bundles,
    lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
    hasMore: snapshot.docs.length === pageSize
  };
};

export const fetchAllBundles = async (): Promise<Bundle[]> => {
  const bundlesRef = collection(db, COLLECTION_NAME);
  const snapshot = await getDocs(query(bundlesRef, orderBy('createdAt', 'desc')));
  return snapshot.docs.map(processBundleDoc);
};

export const fetchBundleById = async (bundleId: string): Promise<Bundle | null> => {
  const bundleRef = doc(db, COLLECTION_NAME, bundleId);
  const bundleSnap = await getDoc(bundleRef);

  if (!bundleSnap.exists()) {
    return null;
  }

  return processBundleDoc(bundleSnap);
};

export const createBundle = async (payload: BundleInput): Promise<string> => {
  const bundlesRef = collection(db, COLLECTION_NAME);

  const data = {
    ...payload,
    displayOrder: normalizeDisplayOrder(payload.displayOrder),
    prices: ensurePriceStructure(payload.prices),
    defaultAddress: payload.defaultAddress || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    stripe: {
      needsStripeSync: true
    }
  };

  const docRef = await addDoc(bundlesRef, data);
  return docRef.id;
};

export const updateBundle = async (bundleId: string, updates: Partial<BundleInput>): Promise<void> => {
  const bundleRef = doc(db, COLLECTION_NAME, bundleId);

  const data: Record<string, any> = {
    ...updates,
    updatedAt: serverTimestamp()
  };

  if (updates.prices) {
    data.prices = ensurePriceStructure(updates.prices as BundlePriceMap);
    data['stripe.needsStripeSync'] = true;
  }

  if (updates.items || updates.purchaseTypes || updates.addressPolicy || updates.shippingConfig) {
    data['stripe.needsStripeSync'] = true;
  }

  if (updates.defaultAddress !== undefined) {
    data.defaultAddress = updates.defaultAddress;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'displayOrder')) {
    data.displayOrder = normalizeDisplayOrder((updates as BundleInput).displayOrder);
  }

  await updateDoc(bundleRef, data);
};

export const deleteBundle = async (bundleId: string): Promise<void> => {
  const bundleRef = doc(db, COLLECTION_NAME, bundleId);
  await deleteDoc(bundleRef);
};

export const markBundlesPublished = async (bundleIds: string[], published: boolean): Promise<void> => {
  if (bundleIds.length === 0) return;
  const batch = writeBatch(db);

  bundleIds.forEach((bundleId) => {
    const bundleRef = doc(db, COLLECTION_NAME, bundleId);
    batch.update(bundleRef, {
      published,
      updatedAt: serverTimestamp()
    });
  });

  await batch.commit();
};

export const triggerBundleStripeSync = async (bundleId: string, force = false): Promise<any> => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    throw new Error('API base URL is not configured');
  }

  const response = await fetch(`${baseUrl}/bundles/${bundleId}/stripe-sync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ force })
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.error || 'Failed to sync bundle with Stripe');
  }

  return response.json();
};

export const buildEmptyBundle = (): BundleInput => ({
  name: '',
  slug: '',
  description: '',
  images: [],
  heroImage: undefined,
  tags: [],
  category: undefined,
  displayOrder: null,
  purchaseTypes: {
    oneTime: true,
    subscription: false
  },
  prices: {
    usd: { oneTime: null, subscription: null },
    cad: { oneTime: null, subscription: null },
    gbp: { oneTime: null, subscription: null }
  },
  items: [],
  shippingConfig: {
    oneTimeTierId: null,
    subscriptionTierId: null
  },
  addressPolicy: 'fixed',
  published: false,
  disabled: false,
  visibleFrom: null,
  visibleTo: null
});
